import {
	DafStepUpAuthStrategyConfig,
	DafAuthConfig,
	XV1App,
	XV2App,
	IDafOnboardingAuthStrategyConfig,
	IDafTokenRefreshStrategyConfig,
	getShellActions,
	DafLoginType,
	DafGrantType,
	setAuthConfig,
	getAuthConfig,
	IDafTransactionStepUpAuthStrategyConfig,
	StepUpIdentityMiniAppConfig,
} from '@nab/x-runtime';
import { CustomDafUsernamePwdStrategyConfig } from './custom-daf-username-pwd-auth-strategy';
import constants from 'util/constants';

export const stepUpConfig = (assetManifestUrl, tenant, clientId): DafStepUpAuthStrategyConfig<DafAuthConfig<XV1App | XV2App>> => {
	return {
		assetManifestUrl: assetManifestUrl,
		appConfig: {
			tenant: tenant,
			displayAsModal: true,
			clientId: clientId,
			scope: `idp:user frauddetect:self-service`,
		} as StepUpIdentityMiniAppConfig,
		getHostElement: () => Promise.resolve(document.querySelector<HTMLElement>("mini-app-loader")),
		parentTokenAuthConfig: {
			context: 'external',
			tokenSource: 'DAF',
			tokenType: 'Bearer',
			isSeed: true
		}
	}
};

export const customStepUpConfig = (assetManifestUrl, tenant, clientId): DafStepUpAuthStrategyConfig<DafAuthConfig<XV1App | XV2App>> => {
	return {
		assetManifestUrl: assetManifestUrl,
		appConfig: {
			tenant: tenant,
			displayAsModal: true,
			// clientId and scope will be set dynamically by CustomDafStepUpAuthStrategy
			// based on whether we're using shell token or mini-app token
		} as StepUpIdentityMiniAppConfig,
		getHostElement: () => Promise.resolve(document.querySelector<HTMLElement>("mini-app-loader")),
		parentTokenAuthConfig: {
			context: 'external',
			tokenSource: 'DAF',
			tokenType: 'Bearer',
			isSeed: true,
			clientId: clientId,
			scopes: ['idp:user', 'frauddetect:self-service']
		}
	}
};

export const transactionStepUpConfig = (tenant, assetManifestUrl): IDafTransactionStepUpAuthStrategyConfig => {
	return {
		assetManifestUrl: assetManifestUrl,
		getHostElement: () => Promise.resolve(document.querySelector<HTMLElement>("mini-app-loader")),
		appConfig: {
			displayAsModal: true,
			tenant: tenant,
			initialDeliveryMethod: 'SMS'
		}
	}
};

export const onboardingConfig = (clientId, assetManifestUrl, tenant, external): IDafOnboardingAuthStrategyConfig => {
	return {
		clientId: clientId,
		scopes: `idp:user ${external.scope}`,
		assetManifestUrl: assetManifestUrl,
		appConfig: {
			tenant: `${tenant}prospect`
		},
		getHostElement: () => Promise.resolve(document.querySelector<HTMLElement>("mini-app-loader")),
		onSuccess: () => {
			const { dispatchEvent } = getShellActions();
			const onboardingAuthConfig = {
				...getAuthConfig(),
				flags: {
					loginType: DafLoginType.USER_ONBOARDING
				}
			}
			dispatchEvent(constants.EVENT_UPDATE_TOKEN_PROVIDER, onboardingAuthConfig);
			setAuthConfig(onboardingAuthConfig);
		},
		onFailure: (e) => console.error(`DafOnboardingAuthStrategy error: ${e}`)
	}
};

export const refreshConfig = (clientId, tenant, external): IDafTokenRefreshStrategyConfig => {
	return {
		customerTenant: tenant,
		prospectTenant: `${tenant}prospect`,
		clientId: clientId,
		parentTokenAuthConfig: {
			context: 'external',
			tokenSource: 'DAF',
			tokenType: 'Bearer',
			isSeed: true
		},
		scopes: `idp:user ${external.scope}`,
		onSuccess: () => {
			const refreshAuthConfig = {
				...getAuthConfig(),
				grantType: DafGrantType.nabAnonymous,
				flags: {
					loginType: DafLoginType.PROSPECT_REFRESH
				}
			}
			setAuthConfig(refreshAuthConfig)
		},
		onFailure: (e) => console.error(`DafTokenRefreshStrategy error: ${e}`)
	}
}

export const prospectLoginConfig = (clientId, tenant, environment, assetManifestUrl, external): CustomDafUsernamePwdStrategyConfig => {
	return {
		appConfig: {
			environment: environment,
			displayAsModal: true,
			/**
			     * IB prospect login modal must remain non-closable.
			     * DE364376: In SIT5 Save & Resume, backdrop-click can result in an empty page.
			     *
			     * If additional prospect tenants are introduced, validate whether
			     * `disableModalClose` should also be enabled for those flows.
			     */
			disableModalClose: true,
			tenant: `${tenant}prospect`,
			clientId: clientId,
			scope: `idp:user ${external.scope}`,
		},
		assetManifestUrl: assetManifestUrl,
		getHostElement: () => Promise.resolve(document.querySelector<HTMLElement>("mini-app-loader")),
		onSuccess: () => {
			const { dispatchEvent } = getShellActions();
			const prospectLoginConfig = {
				...getAuthConfig(),
				grantType: DafGrantType.UsernamePassword
			}
			dispatchEvent(constants.EVENT_UPDATE_TOKEN_PROVIDER, prospectLoginConfig);
			setAuthConfig(prospectLoginConfig);
		},
	}
};

export const userPassConfig = (clientId, tenant, environment, assetManifestUrl, external): CustomDafUsernamePwdStrategyConfig => {
	return {
		appConfig: {
			environment: environment,
			displayAsModal: true,
			tenant: tenant,
			clientId: clientId,
			scope: `idp:user ${external.scope}`,
		},
		assetManifestUrl: assetManifestUrl,
		getHostElement: () => Promise.resolve(document.querySelector<HTMLElement>("mini-app-loader")),
		onSuccess: () => {
			const { updateApps } = getShellActions();
			updateApps({
				authConfig: {
					grantType: DafGrantType.UsernamePassword
				} as DafAuthConfig,
			});
		},
	}
};